import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { DollarSign, Save, Loader2, Info } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminPricing = () => {
  const [settings, setSettings] = useState({
    min_markup_percent: 10,
    target_markup_percent: 45
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Example calculation
  const [exampleCost, setExampleCost] = useState(100);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/pricing`);
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching pricing settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (settings.min_markup_percent < 0) {
      toast.error('Il margine minimo non può essere negativo');
      return;
    }
    if (settings.target_markup_percent < settings.min_markup_percent) {
      toast.error('Il margine target deve essere maggiore o uguale al minimo');
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API}/admin/pricing`, settings);
      toast.success('Impostazioni salvate');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante il salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const minPrice = exampleCost * (1 + settings.min_markup_percent / 100);
  const targetPrice = exampleCost * (1 + settings.target_markup_percent / 100);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 bg-muted rounded animate-pulse" />
        <div className="h-64 bg-muted rounded-xl animate-pulse" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-pricing">
      <div>
        <h1 className="text-3xl font-bold font-serif">Impostazioni Prezzi</h1>
        <p className="text-muted-foreground mt-1">Configura i margini di vendita automatici</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              Margini di Vendita
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="min_markup">Margine Minimo (%)</Label>
              <Input
                id="min_markup"
                type="number"
                min="0"
                step="1"
                value={settings.min_markup_percent}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  min_markup_percent: parseFloat(e.target.value) || 0
                }))}
                data-testid="min-markup-input"
              />
              <p className="text-xs text-muted-foreground">
                Nessun prodotto può essere venduto sotto questo margine
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="target_markup">Margine Target (%)</Label>
              <Input
                id="target_markup"
                type="number"
                min="0"
                step="1"
                value={settings.target_markup_percent}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  target_markup_percent: parseFloat(e.target.value) || 0
                }))}
                data-testid="target-markup-input"
              />
              <p className="text-xs text-muted-foreground">
                Margine applicato ai prodotti popolari/in evidenza
              </p>
            </div>

            <Button
              className="w-full"
              onClick={handleSave}
              disabled={saving}
              data-testid="save-pricing-btn"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Salva Impostazioni
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Calculator */}
        <Card>
          <CardHeader>
            <CardTitle>Calcolatore Prezzi</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="example_cost">Costo Fornitore (€)</Label>
              <Input
                id="example_cost"
                type="number"
                min="0"
                step="0.01"
                value={exampleCost}
                onChange={(e) => setExampleCost(parseFloat(e.target.value) || 0)}
              />
            </div>

            <div className="bg-muted/50 rounded-xl p-6 space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Costo Fornitore:</span>
                <span className="font-mono font-bold">€{exampleCost.toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">
                  Prezzo Minimo (+{settings.min_markup_percent}%):
                </span>
                <span className="font-mono font-medium text-amber-600">
                  €{minPrice.toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">
                  Prezzo Target (+{settings.target_markup_percent}%):
                </span>
                <span className="font-mono font-bold text-green-600">
                  €{targetPrice.toFixed(2)}
                </span>
              </div>
              <div className="border-t border-border pt-4">
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Margine Minimo:</span>
                  <span className="font-mono text-amber-600">
                    €{(minPrice - exampleCost).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between items-center mt-2">
                  <span className="text-muted-foreground">Margine Target:</span>
                  <span className="font-mono text-green-600">
                    €{(targetPrice - exampleCost).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Info */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center shrink-0">
              <Info className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold mb-2">Come funziona il calcolo prezzi</h3>
              <ul className="text-sm text-muted-foreground space-y-2">
                <li>
                  <strong>Prezzo Minimo:</strong> Il prezzo più basso possibile per ogni prodotto. 
                  Non può mai essere inferiore a <code>costo × (1 + margine_minimo%)</code>.
                </li>
                <li>
                  <strong>Prezzo Target:</strong> Il prezzo di default applicato ai prodotti popolari 
                  o in evidenza. Calcolato come <code>costo × (1 + margine_target%)</code>.
                </li>
                <li>
                  <strong>Override Manuale:</strong> Puoi impostare un prezzo personalizzato per 
                  ogni singolo prodotto, ma non potrà mai scendere sotto il prezzo minimo.
                </li>
                <li>
                  <strong>Prodotti Normali:</strong> Venduti al prezzo minimo.
                </li>
                <li>
                  <strong>Prodotti Popolari/Featured:</strong> Venduti al prezzo target.
                </li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminPricing;
